import React, { useState } from "react";
import {
  Briefcase,
  ClipboardList,
  Building,
  UserCheck,
  Calendar,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Mail,
  Award,
  FileText,
  Users,
  Edit,
  Trash2,
  Key,
  UserPlus,
} from "lucide-react";
import CreateHRModal from "../../components/AdminPanel/CreateHRModal";
import EditUserModal from "../../components/AdminPanel/EditUserModal";
import DeleteUserModal from "../../components/AdminPanel/DeleteUserModal";
import PasswordResetModal from "../../components/AdminPanel/PasswordResetModal";

const HRRecruitersTab = ({
  hrUsers,
  tests,
  testResults,
  expandedSections,
  toggleSection,
  formatDate,
  getTestsForHR,
  getResultsForTest,
  onRefresh,
}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleEdit = (hr, e) => {
    e.stopPropagation();
    setSelectedUser(hr);
    setEditModalOpen(true);
  };

  const handleDelete = (hr, e) => {
    e.stopPropagation();
    setSelectedUser(hr);
    setDeleteModalOpen(true);
  };

  const handlePasswordReset = (hr, e) => {
    e.stopPropagation();
    setSelectedUser(hr);
    setPasswordResetModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Create HR User Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setCreateModalOpen(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          <span>Create HR User</span>
        </button>
      </div>

      {/* HR Users */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div
          className="p-6 border-b border-gray-200 cursor-pointer flex items-center justify-between"
          onClick={() => toggleSection("hrUsers")}
        >
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Briefcase className="w-5 h-5 mr-2" />
            HR Users & Information ({hrUsers.length})
          </h2>
          {expandedSections.hrUsers ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.hrUsers && (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tests Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {hrUsers.map((hr) => (
                    <tr key={hr.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                            <UserCheck className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {hr.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {hr.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {hr.company || "Not specified"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(hr.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Users className="w-3 h-3 mr-1" />
                          {getTestsForHR(hr.id).length} tests
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => handleEdit(hr, e)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit User"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => handlePasswordReset(hr, e)}
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                            title="Send Password Reset"
                          >
                            <Key className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => handleDelete(hr, e)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete User"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* HR Tests */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div
          className="p-6 border-b border-gray-200 cursor-pointer flex items-center justify-between"
          onClick={() => toggleSection("hrTests")}
        >
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <ClipboardList className="w-5 h-5 mr-2" />
            HR Created Tests ({tests.length})
          </h2>
          {expandedSections.hrTests ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.hrTests && (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Difficulty
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submissions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tests.map((test) => {
                    const creator = hrUsers.find((hr) => hr.id === test.createdBy);
                    const results = getResultsForTest(test.id);
                    return (
                      <tr key={test.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {test.jobTitle || test.title || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {test.company || creator?.company || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              (test.config?.difficulty || test.difficulty) === "hard"
                                ? "bg-red-100 text-red-800"
                                : (test.config?.difficulty || test.difficulty) === "medium"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {test.config?.difficulty || test.difficulty || "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(test.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <Users className="w-3 h-3 mr-1" />
                            {results.length}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateHRModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={onRefresh}
      />

      <EditUserModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={onRefresh}
        user={selectedUser}
        userType="hr"
      />

      <DeleteUserModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={onRefresh}
        user={selectedUser}
        userType="hr"
      />

      <PasswordResetModal
        isOpen={passwordResetModalOpen}
        onClose={() => {
          setPasswordResetModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />
    </div>
  );
};

export default HRRecruitersTab;
